import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FiSave, FiArrowLeft, FiGlobe } from 'react-icons/fi';

const CareerEditor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [activeTab, setActiveTab] = useState('en'); // 'en' | 'ar'
    const [formData, setFormData] = useState({
        title: '',
        title_ar: '',
        department: '',
        department_ar: '',
        location: 'Manama, Bahrain',
        location_ar: 'المنامة، البحرين',
        type: 'Full-time',
        type_ar: 'دوام كامل',
        salary: '',
        salary_ar: '',
        description: '',
        description_ar: '',
        requirements: '',
        requirements_ar: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            fetch(`/api/careers/read.php?id=${id}`)
                .then(res => res.json())
                .then(data => {
                    if (data && data.title) {
                        setFormData({
                            title: data.title || '',
                            title_ar: data.title_ar || '',
                            department: data.department || '',
                            department_ar: data.department_ar || '',
                            location: data.location || 'Manama, Bahrain',
                            location_ar: data.location_ar || 'المنامة، البحرين',
                            type: data.type || 'Full-time',
                            type_ar: data.type_ar || 'دوام كامل',
                            salary: data.salary || '',
                            salary_ar: data.salary_ar || '',
                            description: data.description || '',
                            description_ar: data.description_ar || '',
                            requirements: Array.isArray(data.requirements) ? data.requirements.join('\n') : (data.requirements || ''),
                            requirements_ar: Array.isArray(data.requirements_ar) ? data.requirements_ar.join('\n') : (data.requirements_ar || '')
                        });
                    }
                })
                .catch(err => console.error('Failed to load job posting:', err))
                .finally(() => setIsLoading(false));
        }
    }, [id, isEditMode]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const reqArray = typeof formData.requirements === 'string'
            ? formData.requirements.split('\n').filter(line => line.trim() !== '')
            : formData.requirements;

        const reqArArray = typeof formData.requirements_ar === 'string'
            ? formData.requirements_ar.split('\n').filter(line => line.trim() !== '')
            : formData.requirements_ar;

        const payload = isEditMode
            ? { ...formData, id, requirements: reqArray, requirements_ar: reqArArray }
            : { ...formData, requirements: reqArray, requirements_ar: reqArArray };

        try {
            const url = isEditMode ? '/api/careers/update.php' : '/api/careers/create.php';

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(isEditMode ? 'Job updated successfully' : 'Job posted successfully');
                navigate('/admin/careers');
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('API Error:', response.status, response.statusText, errorData);
                toast.error(`Failed to ${isEditMode ? 'update' : 'post'} job: ${errorData.message || response.statusText}`);
            }
        } catch (error) {
            console.error('Network/Code Error:', error);
            toast.error('Failed to connect to the server. Check console for details.');
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/careers')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <FiArrowLeft size={24} className="text-gray-500" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800">{isEditMode ? 'Edit Job Posting' : 'Post New Job'}</h1>
                </div>

                {/* Language Switcher Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-xl">
                    <button
                        type="button"
                        onClick={() => setActiveTab('en')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === 'en' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        English (EN)
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('ar')}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'ar' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        <FiGlobe size={14} /> العربية (AR)
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-8 space-y-6">
                {/* Tab: English Content */}
                {activeTab === 'en' && (
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Job Title (English) *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g. Cleaning Supervisor"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Department (English)</label>
                                <input
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    placeholder="e.g. Operations / Facility Management"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Location (English)</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    placeholder="e.g. Manama, Bahrain"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="Full-time">Full-time</option>
                                    <option value="Part-time">Part-time</option>
                                    <option value="Contract">Contract</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Salary (English)</label>
                                <input
                                    type="text"
                                    name="salary"
                                    value={formData.salary}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="e.g. Competitive / BD 250 - 350"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description (English) *</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Detailed job summary and role overview..."
                                required
                            ></textarea>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Requirements (English, one per line)</label>
                            <textarea
                                name="requirements"
                                value={formData.requirements}
                                onChange={handleChange}
                                rows="6"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Minimum 2 years experience in commercial cleaning&#10;Valid Bahrain driving license&#10;Good communication skills"
                            ></textarea>
                        </div>
                    </div>
                )}

                {/* Tab: Arabic Content */}
                {activeTab === 'ar' && (
                    <div className="space-y-6" dir="rtl">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">المسمى الوظيفي (بالعربية)</label>
                                <input
                                    type="text"
                                    name="title_ar"
                                    value={formData.title_ar}
                                    onChange={handleChange}
                                    placeholder="مثال: مشرف تنظيفات"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">القسم / الإدارة (بالعربية)</label>
                                <input
                                    type="text"
                                    name="department_ar"
                                    value={formData.department_ar}
                                    onChange={handleChange}
                                    placeholder="مثال: إدارة العمليات / إدارة المرافق"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الموقع (بالعربية)</label>
                                <input
                                    type="text"
                                    name="location_ar"
                                    value={formData.location_ar}
                                    onChange={handleChange}
                                    placeholder="مثال: المنامة، البحرين"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">نوع العمل (بالعربية)</label>
                                <select
                                    name="type_ar"
                                    value={formData.type_ar}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                >
                                    <option value="دوام كامل">دوام كامل</option>
                                    <option value="دوام جزئي">دوام جزئي</option>
                                    <option value="عقد">عقد</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الراتب (بالعربية)</label>
                                <input
                                    type="text"
                                    name="salary_ar"
                                    value={formData.salary_ar}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                    placeholder="مثال: رواتب مجزية ومنافسة"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الوصف الوظيفي (بالعربية)</label>
                            <textarea
                                name="description_ar"
                                value={formData.description_ar}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                placeholder="نبذة عامة وتفاصيل الدور الوظيفي..."
                            ></textarea>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">متطلبات الوظيفة (بالعربية، سطر واحد لكل متطلب)</label>
                            <textarea
                                name="requirements_ar"
                                value={formData.requirements_ar}
                                onChange={handleChange}
                                rows="6"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-right"
                                placeholder="خبرة لا تقل عن سنتين في مجال التنظيف التجاري&#10;رخصة قيادة بحرينية سارية المفعول&#10;مهارات تواصل وإشراف ممتازة"
                            ></textarea>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                        {activeTab === 'en' ? 'Switch to العربية (AR) tab to provide Arabic translations.' : 'انتقل إلى تبويب English (EN) لتعديل النسخة الإنجليزية.'}
                    </p>
                    <button
                        type="submit"
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm"
                    >
                        <FiSave />
                        <span>{isEditMode ? 'Update Job' : 'Post Job'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CareerEditor;
